import { useEffect, useState } from 'react'
import { useEditMode } from '../contexts/EditModeContext'
import { lumi } from '../lib/lumi'
import {Loader2, ImageIcon, Pencil} from 'lucide-react'
import toast from 'react-hot-toast'
import MediaPickerModal from './MediaPickerModal'

interface EditableImageProps {
  /** Collection name in lumi.entities */
  collection: string
  /** Document _id */
  documentId: string
  /** Field path, supports nested like "content.image" */
  field: string
  /** Current image URL (can be empty) */
  value?: string
  /** CSS class for the wrapper */
  className?: string
  /** CSS class for the <img> element */
  imgClassName?: string
  /** Alt text */
  alt?: string
  /** Fallback content when no value and not in edit mode */
  fallback?: React.ReactNode
  /** Callback after successful save */
  onSave?: (newUrl: string) => void
}

const EditableImage = ({
  collection,
  documentId,
  field,
  value = '',
  className = '',
  imgClassName = '',
  alt = '',
  fallback,
  onSave,
}: EditableImageProps) => {
  const { isEditMode } = useEditMode()
  const [saving, setSaving] = useState(false)
  const [localUrl, setLocalUrl] = useState(value)
  const [pickerOpen, setPickerOpen] = useState(false)

  useEffect(() => {
    setLocalUrl(value)
  }, [value])

  const currentUrl = localUrl || value

  const handleClick = () => {
    if (!isEditMode || !documentId) return
    setPickerOpen(true)
  }

  const handleSelect = async (newUrl: string) => {
    if (!newUrl || newUrl === currentUrl) {
      setPickerOpen(false)
      return
    }

    setSaving(true)
    try {
      // Build update payload supporting nested fields
      const parts = field.split('.')
      const updateData: any = {}

      if (parts.length === 1) {
        updateData[field] = newUrl
      } else {
        // Fetch latest doc to deep-merge nested value
        let doc: any = null
        try {
          doc = await (lumi.entities as any)[collection]?.get?.(documentId)
        } catch {
          /* ignore, fallback to list */
        }
        if (!doc) {
          const res = await (lumi.entities as any)[collection].list({
            filter: { _id: documentId },
            limit: 1,
          })
          doc = res?.list?.[0]
        }
        const topKey = parts[0]
        const nested = JSON.parse(JSON.stringify(doc?.[topKey] || {}))
        let cur: any = nested
        for (let i = 1; i < parts.length - 1; i++) {
          cur[parts[i]] = { ...(cur[parts[i]] || {}) }
          cur = cur[parts[i]]
        }
        cur[parts[parts.length - 1]] = newUrl
        updateData[topKey] = nested
      }
      updateData.updatedAt = new Date().toISOString()

      await (lumi.entities as any)[collection].update(documentId, updateData)

      setLocalUrl(newUrl)
      onSave?.(newUrl)
      toast.success('Image updated!', {
        style: { background: '#2C2C2C', color: '#fff', borderRadius: '8px' },
        duration: 1500,
      })
      setPickerOpen(false)
    } catch (err: any) {
      console.error('Save error:', err)
      toast.error(err?.message || 'Could not update image')
    } finally {
      setSaving(false)
    }
  }

  // Not in edit mode: just display
  if (!isEditMode || !documentId) {
    if (currentUrl) {
      return <img src={currentUrl} alt={alt} className={`${className} ${imgClassName}`} />
    }
    return <div className={className}>{fallback}</div>
  }

  // Edit mode
  return (
    <>
      <div
        className={`${className} relative group/editable-img cursor-pointer`}
        onClick={handleClick}
      >
        {currentUrl ? (
          <img src={currentUrl} alt={alt} className={imgClassName} />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-400">
            {fallback || (
              <div className="flex flex-col items-center gap-2">
                <ImageIcon size={32} />
                <span className="text-sm">No image</span>
              </div>
            )}
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-[#7F1734]/0 group-hover/editable-img:bg-[#7F1734]/40 transition-all duration-200 flex items-center justify-center pointer-events-none z-20 rounded-[inherit]">
          <div className="opacity-0 group-hover/editable-img:opacity-100 transition-opacity duration-200 flex items-center gap-2 px-4 py-2 bg-white rounded-lg shadow-lg">
            {saving ? (
              <>
                <Loader2 size={16} className="animate-spin text-[#7F1734]" />
                <span className="text-sm font-medium text-[#2C2C2C]">Saving...</span>
              </>
            ) : (
              <>
                <Pencil size={16} className="text-[#7F1734]" />
                <span className="text-sm font-medium text-[#2C2C2C]">Change image</span>
              </>
            )}
          </div>
        </div>

        {/* Dashed border */}
        <div className="absolute inset-0 border-2 border-dashed border-[#7F1734]/0 group-hover/editable-img:border-[#7F1734]/60 transition-all duration-200 pointer-events-none rounded-[inherit] z-10" />

        {/* Loader overlay while saving */}
        {saving && (
          <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-30 rounded-[inherit]">
            <Loader2 size={28} className="animate-spin text-[#7F1734]" />
          </div>
        )}
      </div>

      <MediaPickerModal
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={handleSelect}
        currentUrl={currentUrl}
        title="Choose or upload an image"
      />
    </>
  )
}

export default EditableImage
